/**
 * The cloud's status view (PLAN §10.2's status path): what the cloud knows
 * about the caller's tenant's sites and nodes — when it last heard from each
 * node's site link, and that link's state as the cloud sees it. The SPA's
 * connectivity panel reads it to fill the "Node→cloud" column when it cannot
 * reach a node itself (the "local nodes unreachable from here" case), and
 * `acp status` prints it.
 *
 * Cloud only (`routes.ts` spreads these into `ROUTES`; `@norna/handlers`'
 * rundown API excludes them, as it excludes the tenancy table). Every site of
 * the tenant — the automatic cloud site included — with its active nodes (a
 * revoked node is `GET /sites/{siteId}/nodes`' to show).
 *
 * Typed with `satisfies` against a type-only import, as `tenancyRoutes.ts`.
 */
import { openEnum } from './openEnum.js';
import { z } from 'zod';
import type { RouteDef } from './routes.js';
import { witnessInForceWireSchema } from './witness.js';
import { boundedIdWireSchema, isoTimestampWireSchema, siteLinkStateWireSchema } from './primitives.js';

/**
 * A node's role: an on-prem site's `main`/`backup`/`witness` (D9, D21), or `cloud` for a cloud production's task (§9.1).
 * Open (D37 §4.3): `witness` was added here once (bbc09b3) and would have failed every older SPA's read.
 */
const nodeRoleWireSchema = openEnum(['main', 'backup', 'cloud', 'witness']);

const statusNodeWireSchema = z
  .object({
    nodeId: boundedIdWireSchema,
    role: nodeRoleWireSchema,
    /** When the cloud last heard from this node's site link (ISO), or null if it never has. */
    lastSeen: isoTimestampWireSchema.nullable(),
    linkState: siteLinkStateWireSchema,
    /** What operators call it (wave 4b-1). */
    label: z.string().max(128),
    /**
     * Its local HTTPS API (D16: `https://n-<id>.<tenant>.local.<stage domain>`), or null before it
     * has a name — what the SPA's connectivity manager watches and may fail over to (PLAN §10.2).
     */
    apiUrl: z.url().nullable(),
    /** Which witness is in force on the node and its expected failover time, from its last health report (wave 4c); null before one. */
    witness: witnessInForceWireSchema.nullable().optional(),
    /**
     * Whether the certificate the cloud last delivered to the node is one browsers trust (D16: Let's Encrypt
     * production, not staging); null before one was delivered. What the SPA's "unreachable" hint uses to tell a
     * certificate problem from a name that does not resolve. Absent from an older cloud.
     */
    certTrusted: z.boolean().nullable().optional(),
    /**
     * Whether the caller is PROBABLY on the node's network: the caller's public address is the one the node's site
     * link comes from (one NAT). `false`: probably not; null: unknown (no link, no address, or IPv4 against IPv6).
     * A heuristic for the SPA's hint only — never an access decision. Absent from an older cloud.
     */
    sameNetwork: z.boolean().nullable().optional(),
  })
  .strict();

const statusSiteWireSchema = z
  .object({
    siteId: boundedIdWireSchema,
    nodes: z.array(statusNodeWireSchema),
  })
  .strict();

export const statusResponseWireSchema = z.object({ sites: z.array(statusSiteWireSchema) }).strict();

export type StatusResponseWire = z.infer<typeof statusResponseWireSchema>;
export type StatusNodeWire = z.infer<typeof statusNodeWireSchema>;

export const STATUS_ROUTES = {
  getStatus: {
    id: 'getStatus',
    method: 'GET',
    path: '/status',
    tag: 'status',
    summary: 'The tenant’s sites and nodes, as the cloud sees them',
    description:
      'Every site of the caller’s tenant with its active nodes: role, when the cloud last heard from the node’s site link, and that link’s state (PLAN §10.2) — `connected` while its socket is open and it was heard from within three heartbeats. The cloud’s own liveness is the unauthenticated `GET /health`, outside this table.',
    params: null,
    query: null,
    body: null,
    response: statusResponseWireSchema,
    successStatus: 200,
    permission: null,
    permissionScope: 'tenant',
    errors: [],
  },
} as const satisfies Record<string, RouteDef>;
